import { useRef, useState } from 'react';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { Box, IconButton, Paper, Stack, Typography, Modal, Backdrop, Fade } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

import 'swiper/css';
import 'swiper/css/zoom';

import { Zoom } from 'swiper/modules';

export const ImageVisualizer = (
    {
        imageArray,
        imageAlt,
        clickOutsideHandler
    }:
        {

            imageArray: string[],
            imageAlt: string,
            clickOutsideHandler: () => void
        }
) => {
    const [imageToShow, setImageToShow] = useState<string>(imageArray[0]);
    const [indexCurrentImage, setIndexCurrentImage] = useState<number>(0);

    const swiperRef = useRef<SwiperRef>(null);

    const changeImageHandler = (newIndex: number) => {
        setImageToShow(imageArray[newIndex]);
        setIndexCurrentImage(newIndex);
        swiperRef.current?.swiper.zoom.out();
    }

    return (
        <Modal
            open={true}
            onClose={clickOutsideHandler}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
                backdrop: {
                    timeout: 500,
                    sx: { bgcolor: 'rgba(0, 0, 0, 0.85)' }
                }
            }}
        >
            <Fade in={true}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95vw', md: '90vw' },
                    height: { xs: '85vh', md: '85vh' },
                    outline: 'none'
                }}>
                    <Swiper
                        zoom={true}
                        modules={[Zoom]}
                        style={{ width: '100%', height: '100%' }}
                        ref={swiperRef}
                    >
                        <SwiperSlide>
                            <Paper
                                elevation={24}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    bgcolor: '#fff',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}
                            >
                                <Box className="swiper-zoom-container" sx={{ height: '100%' }}>
                                    <img src={imageToShow} alt={imageAlt} style={{ objectFit: 'contain', width: '100%', height: '100%' }} />
                                </Box>

                                {/* Botón de cerrar en la esquina superior derecha */}
                                <IconButton
                                    onClick={clickOutsideHandler}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        bgcolor: 'rgba(0,0,0,0.7)',
                                        color: '#fff',
                                        '&:hover': {
                                            bgcolor: 'rgba(0,0,0,0.9)',
                                            transform: 'scale(1.1)'
                                        },
                                        transition: 'all 0.2s ease',
                                        zIndex: 10
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>

                                {/* Controles de zoom */}
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    sx={{
                                        position: 'absolute',
                                        bottom: 16,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        background: 'linear-gradient(135deg, rgba(24, 53, 85, 0.95) 0%, rgba(44, 95, 141, 0.95) 100%)',
                                        borderRadius: 2,
                                        padding: 0.5,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                                    }}
                                >
                                    <IconButton
                                        onClick={() => swiperRef.current?.swiper.zoom.out()}
                                        sx={{
                                            color: '#fff',
                                            '&:hover': {
                                                bgcolor: 'rgba(255,255,255,0.2)',
                                                transform: 'scale(1.1)'
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <ZoomOutIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => swiperRef.current?.swiper.zoom.in()}
                                        sx={{
                                            color: '#fff',
                                            '&:hover': {
                                                bgcolor: 'rgba(255,255,255,0.2)',
                                                transform: 'scale(1.1)'
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <ZoomInIcon />
                                    </IconButton>
                                </Stack>

                                {/* Navegación de imágenes */}
                                {
                                    imageArray.length > 1 &&
                                    <>
                                        {/* Botón anterior */}
                                        <Box sx={{ position: 'absolute', top: '50%', left: 16, transform: 'translateY(-50%)', zIndex: 10 }}>
                                            <IconButton
                                                onClick={() => changeImageHandler(indexCurrentImage - 1)}
                                                disabled={indexCurrentImage === 0}
                                                sx={{
                                                    bgcolor: 'rgba(24, 53, 85, 0.9)',
                                                    color: '#fff',
                                                    width: 56,
                                                    height: 56,
                                                    '&:hover': {
                                                        bgcolor: 'rgba(24, 53, 85, 1)',
                                                        transform: 'scale(1.1)',
                                                        boxShadow: '0 6px 16px rgba(0,0,0,0.4)'
                                                    },
                                                    '&:disabled': {
                                                        bgcolor: 'rgba(0,0,0,0.3)',
                                                        color: 'rgba(255,255,255,0.3)'
                                                    },
                                                    transition: 'all 0.2s ease',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                                                }}
                                            >
                                                <ChevronLeftIcon sx={{ fontSize: 32 }} />
                                            </IconButton>
                                        </Box>

                                        {/* Botón siguiente */}
                                        <Box sx={{ position: 'absolute', top: '50%', right: 16, transform: 'translateY(-50%)', zIndex: 10 }}>
                                            <IconButton
                                                onClick={() => changeImageHandler(indexCurrentImage + 1)}
                                                disabled={indexCurrentImage === imageArray.length - 1}
                                                sx={{
                                                    bgcolor: 'rgba(24, 53, 85, 0.9)',
                                                    color: '#fff',
                                                    width: 56,
                                                    height: 56,
                                                    '&:hover': {
                                                        bgcolor: 'rgba(24, 53, 85, 1)',
                                                        transform: 'scale(1.1)',
                                                        boxShadow: '0 6px 16px rgba(0,0,0,0.4)'
                                                    },
                                                    '&:disabled': {
                                                        bgcolor: 'rgba(0,0,0,0.3)',
                                                        color: 'rgba(255,255,255,0.3)'
                                                    },
                                                    transition: 'all 0.2s ease',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                                                }}
                                            >
                                                <ChevronRightIcon sx={{ fontSize: 32 }} />
                                            </IconButton>
                                        </Box>

                                        {/* Indicador de página */}
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 16,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            bgcolor: 'rgba(0,0,0,0.7)',
                                            px: 2,
                                            py: 1,
                                            borderRadius: 2,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                        }}>
                                            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                                                {indexCurrentImage + 1} / {imageArray.length}
                                            </Typography>
                                        </Box>
                                    </>
                                }
                            </Paper>

                        </SwiperSlide>
                    </Swiper>
                </Box>
            </Fade>
        </Modal>
    )
}